import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Clock, Users, Star, Sparkles } from 'lucide-react';

const CourseCard = ({ course, showProgress = false }) => {
  const totalDuration = course.lessons?.reduce((acc, l) => acc + (l.duration_minutes || 0), 0) || course.total_duration_minutes || 0;
  const hours = Math.floor(totalDuration / 60);
  const minutes = totalDuration % 60;
  const durationText = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  const isFree = course.is_free || course.price === 0 || course.price === '0';

  const levelColors = {
    beginner: 'bg-green-100 text-green-800',
    intermediate: 'bg-yellow-100 text-yellow-800',
    advanced: 'bg-red-100 text-red-800'
  };

  const hasLessonPricing = course.lesson_price && course.price;
  const lessonCount = course.lessons?.length || 0;
  const savingsPercent = hasLessonPricing 
    ? Math.round(((lessonCount * course.lesson_price - course.price) / (lessonCount * course.lesson_price)) * 100)
    : 0;

  return (
    <Link to={`/courses/${course.id}`} data-testid={`course-card-${course.id}`}>
      <Card className={`group overflow-hidden border bg-white hover:shadow-xl hover:shadow-indigo-500/10 transition-all duration-300 h-full ${isFree ? 'border-emerald-300 ring-1 ring-emerald-200' : 'border-slate-200'}`}>
        <div className="relative aspect-video overflow-hidden">
          <img 
            src={course.thumbnail_url || 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800'} 
            alt={course.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          {showProgress && course.progress && (
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-slate-200">
              <div 
                className="h-full bg-secondary transition-all duration-300"
                style={{ width: `${course.progress.progress_percent || 0}%` }}
              />
            </div>
          )}
          <div className="absolute top-3 left-3 flex gap-2">
            <Badge className={`${levelColors[course.level] || levelColors.beginner} border-0`}>
              {course.level}
            </Badge>
            {course.is_platform_course && (
              <Badge className="bg-primary/90 text-white border-0">
                <Sparkles className="w-3 h-3 mr-1" />
                Original
              </Badge>
            )}
          </div>
          {/* Price Badge */}
          <div className="absolute bottom-3 right-3">
            {isFree ? (
              <Badge className="bg-emerald-500 text-white font-bold border-0 shadow-md text-sm px-3 py-1" data-testid="free-badge">
                FREE
              </Badge>
            ) : (
              <Badge className="bg-white/95 text-slate-900 font-semibold border-0 shadow-sm">
                ${course.price}
                {hasLessonPricing && savingsPercent > 0 && (
                  <span className="ml-1 text-xs text-green-600">({savingsPercent}% off)</span>
                )}
              </Badge>
            )}
          </div>
        </div>
        <CardContent className="p-5">
          <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
            <span className="font-medium text-primary">{course.category}</span>
            {lessonCount > 0 && (
              <>
                <span>•</span>
                <span>{lessonCount} lessons</span>
              </>
            )}
          </div>
          <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2 line-clamp-2 group-hover:text-primary transition-colors">
            {course.title}
          </h3>
          <p className="text-sm text-slate-600 mb-4 line-clamp-2">
            {course.description}
          </p>
          <div className="flex items-center justify-between text-sm text-slate-500">
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {durationText}
              </span>
              <span className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                {course.students_count || 0}
              </span>
            </div>
            {course.rating > 0 && (
              <span className="flex items-center gap-1 text-amber-500">
                <Star className="w-4 h-4 fill-current" />
                {course.rating.toFixed(1)}
              </span>
            )}
          </div>
          {showProgress && course.progress && (
            <div className="mt-3 pt-3 border-t border-slate-100">
              <p className="text-sm font-medium text-primary">
                {Math.round(course.progress.progress_percent || 0)}% complete
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </Link>
  );
};

export default CourseCard;
